import React, { useState, useRef, useEffect, memo, useMemo } from 'react';
import ChartZoomOutPill from './ChartZoomOutPill';

// ═══════════════════════════════════════════════════════════════════════════════
// LAYOUT ENGINE (from SeatingLayoutMap guide)
// SVG viewBox is 0 0 100 100 — all positions computed in that space.
// ═══════════════════════════════════════════════════════════════════════════════

const VIEW_SIZE = 100
const EPSILON = 1e-7
const SEAT_RADIUS = 0.65
const SEAT_RADIUS_SELECTED = 0.65

const clamp = (value, min, max) => Math.max(min, Math.min(max, value))

const getSeatKeyFromSeat = (seat) =>
  seat?.seatKey || `${seat?.sectionId || 'section'}-${seat?.rowLabel || 'row'}-${seat?.seatNumber || 0}`

const getSeatStatus = (seat) => {
  const s = seat?.status || 'available'
  return s === 'held' ? 'hold' : s
}

const toView = (normalized) => clamp(Number(normalized || 0), 0, 1) * VIEW_SIZE

const getSectionLocalPolygon = (section) => {
  const points = Array.isArray(section?.normalizedPoints) ? section.normalizedPoints : []
  if (!points.length) {
    return [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }]
  }
  return points.map((point) => ({
    x: clamp(Number(point?.x || 0), 0, 1),
    y: clamp(Number(point?.y || 0), 0, 1)
  }))
}

const localToGlobal = (sectionCoords, x, y) => ({
  x: toView(sectionCoords.x + x * sectionCoords.width),
  y: toView(sectionCoords.y + y * sectionCoords.height)
})

const polygonToGlobalPoints = (sectionCoords, points) =>
  points.map((point) => localToGlobal(sectionCoords, point.x, point.y))

const toSvgPointString = (points) => points.map((p) => `${p.x},${p.y}`).join(' ')

const getPolygonCentroid = (polygon) => {
  const total = polygon.reduce(
    (acc, point) => ({ x: acc.x + point.x, y: acc.y + point.y }),
    { x: 0, y: 0 }
  )
  return { x: total.x / polygon.length, y: total.y / polygon.length }
}

const pointInPolygon = (point, polygon) => {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x; const yi = polygon[i].y
    const xj = polygon[j].x; const yj = polygon[j].y
    if (((yi > point.y) !== (yj > point.y)) &&
        (point.x < (xj - xi) * (point.y - yi) / (yj - yi + EPSILON) + xi)) {
      inside = !inside
    }
  }
  return inside
}

const nearestPointOnEdge = (point, a, b) => {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const len2 = dx * dx + dy * dy
  if (len2 < EPSILON) return { x: a.x, y: a.y }
  const t = clamp(((point.x - a.x) * dx + (point.y - a.y) * dy) / len2, 0, 1)
  return { x: a.x + t * dx, y: a.y + t * dy }
}

const clampToPolygon = (point, polygon) => {
  if (pointInPolygon(point, polygon)) return point
  let bestDist = Infinity
  let bestPoint = point
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i]
    const b = polygon[(i + 1) % polygon.length]
    const proj = nearestPointOnEdge(point, a, b)
    const dist = Math.hypot(point.x - proj.x, point.y - proj.y)
    if (dist < bestDist) {
      bestDist = dist
      bestPoint = proj
    }
  }
  const centroid = getPolygonCentroid(polygon)
  const toC = { x: centroid.x - bestPoint.x, y: centroid.y - bestPoint.y }
  const cDist = Math.hypot(toC.x, toC.y)
  if (cDist > EPSILON) {
    const nudge = Math.min(2, cDist * 0.03)
    return {
      x: bestPoint.x + (toC.x / cDist) * nudge,
      y: bestPoint.y + (toC.y / cDist) * nudge
    }
  }
  return bestPoint
}

const getTopInnerEdgeAxes = (polygon) => {
  const edges = []
  let minY = Infinity
  let maxY = -Infinity
  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i]
    const b = polygon[(i + 1) % polygon.length]
    const dx = b.x - a.x
    const dy = b.y - a.y
    const length = Math.hypot(dx, dy)
    if (length <= EPSILON) continue
    const midY = (a.y + b.y) / 2
    minY = Math.min(minY, a.y, b.y)
    maxY = Math.max(maxY, a.y, b.y)
    edges.push({ a, b, dx, dy, length, midY })
  }
  const sectionHeight = Math.max(1, maxY - minY)
  const minMidY = edges.reduce((acc, edge) => Math.min(acc, edge.midY), Infinity)
  const topBand = Math.max(20, sectionHeight * 0.16)
  const candidates = edges.filter((edge) => edge.midY <= minMidY + topBand)
  const topEdge = (candidates.length ? candidates : edges).sort((left, right) => {
    const leftHoriz = Math.abs(left.dx)
    const rightHoriz = Math.abs(right.dx)
    if (rightHoriz !== leftHoriz) return rightHoriz - leftHoriz
    if (right.length !== left.length) return right.length - left.length
    return left.midY - right.midY
  })[0]

  if (!topEdge) {
    return {
      origin: polygon[0] || { x: 0, y: 0 },
      axis: { x: 1, y: 0 },
      normal: { x: 0, y: 1 }
    }
  }

  let axis = {
    x: (topEdge.b.x - topEdge.a.x) / topEdge.length,
    y: (topEdge.b.y - topEdge.a.y) / topEdge.length
  }
  if (axis.x < 0) axis = { x: -axis.x, y: -axis.y }

  const origin = {
    x: (topEdge.a.x + topEdge.b.x) / 2,
    y: (topEdge.a.y + topEdge.b.y) / 2
  }

  const centroid = getPolygonCentroid(polygon)
  const candidateNormalA = { x: -axis.y, y: axis.x }
  const toCentroid = { x: centroid.x - origin.x, y: centroid.y - origin.y }
  const dot = candidateNormalA.x * toCentroid.x + candidateNormalA.y * toCentroid.y
  const normal = dot >= 0 ? candidateNormalA : { x: -candidateNormalA.x, y: -candidateNormalA.y }

  return { origin, axis, normal }
}

const cross2D = (a, b) => a.x * b.y - a.y * b.x

const getLinePolygonSegments = (polygon, lineOrigin, lineDir) => {
  const tValues = []
  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i]
    const b = polygon[(i + 1) % polygon.length]
    const seg = { x: b.x - a.x, y: b.y - a.y }
    const delta = { x: a.x - lineOrigin.x, y: a.y - lineOrigin.y }
    const denom = cross2D(lineDir, seg)
    if (Math.abs(denom) <= EPSILON) continue
    const t = cross2D(delta, seg) / denom
    const u = cross2D(delta, lineDir) / denom
    if (u >= -EPSILON && u <= 1 + EPSILON) tValues.push(t)
  }
  tValues.sort((a, b) => a - b)
  const segments = []
  for (let i = 0; i + 1 < tValues.length; i += 2) {
    segments.push({ minT: tValues[i], maxT: tValues[i + 1] })
  }
  return segments
}

/**
 * Find section (and area) in blueprint by sectionId.
 */
function findSectionInBlueprint(blueprint, sectionId) {
  const areas = Array.isArray(blueprint?.areas) ? blueprint.areas : []
  for (const area of areas) {
    const section = area?.sections?.find((s) => s.id === sectionId)
    if (section) return { area, section }
  }
  return null
}

const SHRINK = 1
const ROW_INSET = 0


const getRowAverageSeatY = (row, fallbackIndex) => {
  const yValues = Array.isArray(row?.seats)
    ? row.seats
        .map((seat) => Number(seat?.coordinates?.y))
        .filter(Number.isFinite)
    : []

  if (!yValues.length) return fallbackIndex
  return yValues.reduce((sum, y) => sum + y, 0) / yValues.length
}

const getRowsWithVisualOrder = (section, reverseRowOrder = false) => {
  const rows = Array.isArray(section?.rows) ? section.rows : []
  const orderedRows = reverseRowOrder ? [...rows].reverse() : rows

  return orderedRows.map((row, visualIndex) => ({
    row,
    originalIndex: rows.indexOf(row),
    visualIndex,
    avgY: getRowAverageSeatY(row, visualIndex)
  }))
}

const shouldReverseAreaRows = (area) => {
  const sections = Array.isArray(area?.sections) ? area.sections : []
  let reverseVotes = 0
  let forwardVotes = 0

  sections.forEach((section) => {
    const rows = Array.isArray(section?.rows) ? section.rows : []
    if (rows.length < 2) return

    const firstRowY = getRowAverageSeatY(rows[0], 0)
    const lastRowY = getRowAverageSeatY(rows[rows.length - 1], rows.length - 1)
    if (Math.abs(firstRowY - lastRowY) <= EPSILON) return

    // Smaller blueprint y means visually higher in the section. If the last
    // row in the data is higher than the first row for most sections in this
    // area, draw the area in reversed row order. This uses the chart geometry
    // itself instead of brittle area names like "General Admission".
    if (lastRowY < firstRowY) reverseVotes += 1
    else forwardVotes += 1
  })

  return reverseVotes > forwardVotes
}

/**
 * Compute (x, y) for one seat in a section: row at rowIndex, seat at seatIndexInRow (0-based).
 * Uses same layout math as full row (top edge, depth, segments, spacing).
 */
function computeSeatPositionInSection(globalPolygon, section, row, visualRowIndex, seatIndexInRow) {
  const rows = Array.isArray(section?.rows) ? section.rows : []
  if (!row) return null

  const seatCount = Number(
    row?.seatCount ||
    (Number(row?.seatEnd || 0) - Number(row?.seatStart || 1) + 1) ||
    0
  )
  if (seatCount <= 0 || seatIndexInRow < 0 || seatIndexInRow >= seatCount) return null

  const rowCount = Math.max(rows.length, 1)
  const { origin, axis, normal } = getTopInnerEdgeAxes(globalPolygon)

  const depthValues = globalPolygon.map(
    (point) => (point.x - origin.x) * normal.x + (point.y - origin.y) * normal.y
  )
  const minDepth = Math.min(...depthValues)
  const maxDepth = Math.max(...depthValues)
  const depthRange = Math.max(EPSILON, maxDepth - minDepth)

  const EXTRA_DEPTH = 0.5;

  const depthIndex = rowCount - visualRowIndex
  const progress = depthIndex / (rowCount + 1)
  const offset = minDepth + depthRange * (ROW_INSET + progress * (1 - ROW_INSET * 2)) - EXTRA_DEPTH;
  const lineOrigin = {
    x: origin.x + normal.x * offset,
    y: origin.y + normal.y * offset
  }

  const segments = getLinePolygonSegments(globalPolygon, lineOrigin, axis)
  if (!segments.length) return null

  const shrunkSegments = segments.map((seg) => {
    const center = (seg.minT + seg.maxT) / 2
    const half = ((seg.maxT - seg.minT) / 2) * SHRINK
    return { left: center - half, right: center + half, span: half * 2 }
  })
  const totalSpan = shrunkSegments.reduce((sum, s) => sum + s.span, 0)
  if (totalSpan < EPSILON) return null

  const spacing = totalSpan / (seatCount + 1)
  const seatTValues = []
  for (let i = 0; i < seatCount; i++) {
    let accum = spacing * (i + 1)
    let placed = false
    for (const seg of shrunkSegments) {
      if (accum <= seg.span + EPSILON) {
        seatTValues.push(seg.left + accum)
        placed = true
        break
      }
      accum -= seg.span
    }
    if (!placed) {
      const last = shrunkSegments[shrunkSegments.length - 1]
      seatTValues.push(last.right)
    }
  }
  seatTValues.reverse()

  const HORIZONTAL_OFFSET = 0.2;

  const seatT = seatTValues[seatIndexInRow] + HORIZONTAL_OFFSET;
  const rawPosition = {
    x: lineOrigin.x + axis.x * seatT,
    y: lineOrigin.y + axis.y * seatT
  }
  return clampToPolygon(rawPosition, globalPolygon)
}

/**
 * Builds sectionShapes, rowLabels from blueprint; seatNodes from top-level seats array.
 * Only seats that exist in the seats array are rendered; positions come from blueprint layout.
 */
function makeSeatPositionModel(blueprint, seats) {
  const sectionShapes = []
  const rowLabels = []
  const sectionLayoutCache = new Map() // sectionId -> { area, section, globalPolygon, reverseRowDepth }

  const areas = Array.isArray(blueprint?.areas) ? blueprint.areas : []
  areas.forEach((area) => {
    const sections = Array.isArray(area?.sections) ? area.sections : []
    const reverseAreaRows = shouldReverseAreaRows(area)
    sections.forEach((section) => {
      const sectionCoords = section?.coordinates
      if (!sectionCoords) return

      const localPolygon = getSectionLocalPolygon(section)
      const globalPolygon = polygonToGlobalPoints(sectionCoords, localPolygon)
      const rowsWithVisualOrder = getRowsWithVisualOrder(section, reverseAreaRows)
      const rowVisualIndexByLabel = new Map(
        rowsWithVisualOrder.map(({ row, originalIndex }, visualIndex) => [String(row?.label ?? originalIndex), visualIndex])
      )
      sectionLayoutCache.set(section.id, { area, section, globalPolygon, rowVisualIndexByLabel })
      sectionShapes.push({
        id: section?.id || `${area?.id || 'area'}-section`,
        areaId: area?.id,
        areaName: area?.name,
        sectionName: section?.name,
        points: globalPolygon,
        color: section?.color || area?.color || '#7c3aed'
      })

      const rows = Array.isArray(section?.rows) ? section.rows : []
      const rowsForLayout = rowsWithVisualOrder.length ? rowsWithVisualOrder : rows.map((row, originalIndex) => ({ row, originalIndex }))
      const rowCount = Math.max(rowsForLayout.length, 1)
      const { origin, axis, normal } = getTopInnerEdgeAxes(globalPolygon)

      const depthValues = globalPolygon.map(
        (point) => (point.x - origin.x) * normal.x + (point.y - origin.y) * normal.y
      )
      const minDepth = Math.min(...depthValues)
      const maxDepth = Math.max(...depthValues)
      const depthRange = Math.max(EPSILON, maxDepth - minDepth)

      rowsForLayout.forEach(({ row }, visualRowIndex) => {
        const seatCount = Number(
          row?.seatCount ||
          (Number(row?.seatEnd || 0) - Number(row?.seatStart || 1) + 1) ||
          0
        )
        if (seatCount <= 0) return

        const inset = ROW_INSET
        const depthIndex = rowCount - visualRowIndex
        const progress = depthIndex / (rowCount + 1)
        const offset = minDepth + depthRange * (inset + progress * (1 - inset * 2))
        const lineOrigin = {
          x: origin.x + normal.x * offset,
          y: origin.y + normal.y * offset
        }

        const segments = getLinePolygonSegments(globalPolygon, lineOrigin, axis)
        if (!segments.length) return

        const shrunkSegments = segments.map((seg) => {
          const center = (seg.minT + seg.maxT) / 2
          const half = ((seg.maxT - seg.minT) / 2) * SHRINK
          return { left: center - half, right: center + half, span: half * 2 }
        })
        const totalSpan = shrunkSegments.reduce((sum, s) => sum + s.span, 0)
        if (totalSpan < EPSILON) return

        const spacing = totalSpan / (seatCount + 1)
        const firstSeg = shrunkSegments[0]
        const labelRaw = {
          x: lineOrigin.x + axis.x * (firstSeg.left - spacing * 0.4),
          y: lineOrigin.y + axis.y * (firstSeg.left - spacing * 0.4)
        }
        const labelClamped = clampToPolygon(labelRaw, globalPolygon)
        rowLabels.push({
          id: `${section?.id || 'section'}-${row?.label || visualRowIndex}`,
          sectionId: section?.id,
          areaId: area?.id,
          x: labelClamped.x,
          y: labelClamped.y,
          label: row?.label || ''
        })
      })
    })
  })

  // Build seatNodes from top-level seats array only
  const seatNodes = []
  ;(seats || []).forEach((seat) => {
    const sectionId = seat?.sectionId
    const rowLabel = seat?.rowLabel
    const seatNumber = Number(seat?.seatNumber)
    if (sectionId == null || rowLabel == null || seatNumber == null) return

    const cached = sectionLayoutCache.get(sectionId)
    if (!cached) return
    const { section, globalPolygon, rowVisualIndexByLabel } = cached

    const rows = Array.isArray(section?.rows) ? section.rows : []
    const rowIndex = rows.findIndex((r) => String(r?.label) === String(rowLabel))
    if (rowIndex === -1) return

    const row = rows[rowIndex]
    const seatStart = Number(row?.seatStart ?? 1)
    const seatIndexInRow = seatNumber - seatStart
    if (seatIndexInRow < 0) return

    const visualRowIndex = rowVisualIndexByLabel?.has(String(rowLabel))
      ? rowVisualIndexByLabel.get(String(rowLabel))
      : rowIndex

    const position = computeSeatPositionInSection(
      globalPolygon,
      section,
      row,
      visualRowIndex,
      seatIndexInRow
    )
    if (!position) return

    const mergedSeat = {
      sectionId: section?.id,
      sectionName: section?.name,
      areaId: cached.area?.id,
      areaName: cached.area?.name,
      rowLabel: row?.label,
      seatNumber,
      status: 'available',
      isHandicap: Boolean(row?.seats?.[`${row?.label}-${seatNumber}`]?.isHandicap),
      ...seat
    }
    seatNodes.push({
      key: getSeatKeyFromSeat(mergedSeat),
      seat: mergedSeat,
      x: position.x,
      y: position.y
    })
  })

  return { sectionShapes, rowLabels, seatNodes }
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const SeatingChart = memo(function SeatingChart({
  blueprint,
  seats = [],
  selectedAreaId: controlledAreaId = null,
  selectedSectionId: controlledSectionId = null,
  selectedSeatIds = [],
  onAreaSelect,
  onSectionSelect,
  onSeatToggle,
  onZoomOutToSections,
  imageUrl,
  zoomOutToAreaTrigger = 0,
}) {
  const [internalAreaId, setInternalAreaId] = useState(null);
  const [internalSectionId, setInternalSectionId] = useState(null);
  const [zoomStyle, setZoomStyle] = useState({
    transform: "scale(1) translate(0, 0)",
    transformOrigin: "0 0",
  });
  const containerRef = useRef(null);
  const prevZoomOutTriggerRef = useRef(0);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const updateSize = () => {
      setContainerSize({ width: el.offsetWidth || 0, height: el.offsetHeight || 0 });
    };
    updateSize();
    const ro = new ResizeObserver(updateSize);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const isControlled = typeof onAreaSelect === 'function';
  const selectedAreaId = isControlled ? (controlledAreaId ?? null) : internalAreaId;
  const selectedSectionId = isControlled ? (controlledSectionId ?? null) : internalSectionId;

  const areas = blueprint?.areas ?? []
  const url = imageUrl
  const theRawString = blueprint?.backgroundImage?.dataUrl
  const cleanUri = url
    ? url
    : theRawString
      ? decodeURIComponent(theRawString).replace(/\uFEFF/g, '').trim()
      : ''

  const { sectionShapes, rowLabels, seatNodes } = useMemo(
    () => makeSeatPositionModel(blueprint, seats),
    [blueprint, seats]
  )


  // Get seat counts for a section from the flat seats array
  function getSectionSeatCounts(sectionId) {
    const sectionSeats = seats.filter((s) => s.sectionId === sectionId);
    const available = sectionSeats.filter((s) => s.status === 'available').length;
    const selected = selectedSeatIds.filter((key) =>
      sectionSeats.some((s) => s.seatKey === key)
    ).length;
    return { available, selected, total: sectionSeats.length };
  }

  // Convert blueprint coordinates to SVG points string (viewBox 0 0 100 100).
  // Areas use normalizedPoints (0–1) directly.
  // Sections use coordinates (bbox) + normalizedPoints (0–1 within bbox).
  function calculateSvgPoints(areaOrSection) {
    const points = areaOrSection.normalizedPoints
    if (!points?.length) return ''

    const coords = areaOrSection.coordinates
    if (coords) {
      return points
        .map(
          (p) =>
            `${(coords.x + p.x * coords.width) * 100},${(coords.y + p.y * coords.height) * 100}`
        )
        .join(' ')
    }
    return points.map((p) => `${p.x * 100},${p.y * 100}`).join(' ')
  }

  const SECTION_ZOOM_PADDING = 0.04; // Tighter = zoom in more (default 0.05)
  const applyZoom = (coordinates, padding = 0.05) => {
    const { x, y, width, height } = coordinates;

    let containerAspect = 1.77;
    if (containerRef.current) {
      containerAspect =
        containerRef.current.offsetWidth / containerRef.current.offsetHeight;
    }

    const scaleX = 1 / (width + padding * 2);
    const scaleY = 1 / ((height + padding * 2) * containerAspect);
    const scale = Math.max(scaleX, scaleY);
    const visibleWidth = 1 / scale;
    const visibleHeight = visibleWidth / containerAspect;
    const translateX = -(x - (visibleWidth - width) / 2);
    let translateY = -(y - (visibleHeight - height) / 2);
    // Shift zoomed view 10px upward (move content down in % of container height)
    const containerHeight = containerRef.current?.offsetHeight ?? 400;
    const upShiftPercent = (8 / containerHeight) * 100;
    translateY = translateY + upShiftPercent / 100;

    setZoomStyle({
      transform: `scale(${scale}) translate(${translateX * 100}%, ${
        translateY * 100
      }%)`,
      transformOrigin: "0 0",
    });
  };

  const resetZoom = () => {
    if (!isControlled) {
      // setInternalAreaId(null);
      setInternalSectionId(null);
    }
    setZoomStyle({
      transform: "scale(1) translate(0, 0)",
      transformOrigin: "0 0",
    });
  };

  /** Reset to original picture size only (keeps area/section selection highlighted). Used by "Zoom out to sections". */
  const zoomOutToOriginal = () => {
    setZoomStyle({
      transform: "scale(1) translate(0, 0)",
      transformOrigin: "0 0",
    });
  };

  // When parent controls selection, sync zoom to area/section
  useEffect(() => {
    if (!isControlled) return;
    if (controlledSectionId && blueprint?.areas) {
      for (const area of blueprint.areas) {
        const section = area.sections?.find((s) => s.id === controlledSectionId);
        if (section?.coordinates) {
          applyZoom(section.coordinates, SECTION_ZOOM_PADDING);
          return;
        }
      }
    }
    // Do not zoom when only an area is selected (area = highlight only).
    if (controlledAreaId && !controlledSectionId) return;
    if (!controlledAreaId && !controlledSectionId) {
      resetZoom();
    }
  }, [isControlled, controlledAreaId, controlledSectionId, blueprint?.areas]);


  useEffect(() => {
    if (!isControlled) return
    if (zoomOutToAreaTrigger <= prevZoomOutTriggerRef.current) return
    prevZoomOutTriggerRef.current = zoomOutToAreaTrigger
    if (isControlled && onSectionSelect) onSectionSelect(null, null)
    if (!isControlled) setInternalSectionId(null)
    setZoomStyle({
      transform: "scale(1) translate(0, 0)",
      transformOrigin: "0 0",
    })
  }, [isControlled, onSectionSelect, zoomOutToAreaTrigger])


  function handleSectionClickWithZoom(section, area) {
    if (selectedSectionId === section.id) return
  
    const nextId = section.id
    if (isControlled && onSectionSelect) {
      onSectionSelect(section, area)
    } else {
      setInternalSectionId(nextId)
    }
    if (section.coordinates) {
      applyZoom(section.coordinates, SECTION_ZOOM_PADDING)
    }
  }

  return (
    <div ref={containerRef} className="relative w-full h-full overflow-hidden">
      <div
        style={{
          ...zoomStyle,
          // transition: "transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          transition: "transform 1.4s cubic-bezier(0.25, 0.1, 0.25, 1)",
        }}
        className="relative w-full h-full"
      >
      {cleanUri && (
        <img
          src={cleanUri}
          alt="Seating chart"
          className="w-full h-auto block"
        />
      )}
      {/* TOP LEVEL SECTIONS */}
      {sectionShapes.length > 0 && (
        <svg
          viewBox="0 0 100 100"
          className="absolute inset-0 w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          style={cleanUri ? {} : { position: 'relative', minHeight: 300 }}
        >
          {sectionShapes.map((shape) => {
            const area = areas.find((a) => a.id === shape.areaId)
            const section = area?.sections?.find((s) => s.id === shape.id)
            if (!section || !area) return null

            const isSectionSelected = selectedSectionId === shape.id
            const isAnySectionSelected = selectedSectionId !== null
            const { available, selected } = getSectionSeatCounts(shape.id)
            const hasAvailable = available > 0

            return (
              <React.Fragment key={shape.id}>
                <polygon
                  points={toSvgPointString(shape.points)}
                  fill={hasAvailable ? (section.color || area.color || '#3B82F6') : '#6B7280'}
                  fillOpacity={
                    isSectionSelected
                      // ? 0.15
                      ? 0.15
                      : isAnySectionSelected
                      // ? 0.3
                      ? 0.3
                      // : hasAvailable ? 0.8 : 0.4
                      : hasAvailable ? 0 : 0
                  }
                  stroke={selected > 0 ? '#10B981' : 'white'}
                  strokeWidth={isSectionSelected ? 0.02 : selected > 0 ? 0.15 : 0.05}
                  style={{ vectorEffect: 'non-scaling-stroke' }}
                  className="cursor-pointer hover:opacity-90 transition-all duration-200"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleSectionClickWithZoom(section, area)
                  }}
                />
                {/* {displaySeatNodes */}
                {seatNodes
                  .filter((node) => node.seat?.sectionId === shape.id)
                  .map((item) => {
                    const { seat, x, y, key } = item
                    const status = getSeatStatus(seat)
                    const isSelectedSeat = selectedSeatIds.includes(key) || selectedSeatIds.includes(seat?.seatKey)
                    const fill =
                      isSelectedSeat
                      ? (section.color || area.color || '#FFFFFF')
                        : status === 'available'
                        ? (section.color || area.color || '#FFFFFF')
                        : status === 'sold'
                        ? '#6B7280'
                        : '#888'
                    const radius = isSelectedSeat ? SEAT_RADIUS_SELECTED : SEAT_RADIUS
                    const stroke = isSelectedSeat
                      ? (section.color || area.color || '#10B981')
                      : ""
                    const seatLabel = `${seat?.rowLabel ?? ''}${seat?.seatNumber ?? ''}`
                    const { width: cw, height: ch } = containerSize
                    const aspectRatio = ch > 0 ? cw / ch : 1
                    const ry = radius * aspectRatio

                    return (
                      <React.Fragment key={key}>
                        <ellipse
                          cx={x}
                          cy={y}
                          rx={radius}
                          ry={ry}
                          fill={fill}
                          stroke={stroke}
                          strokeWidth={0.5}
                          style={{
                            vectorEffect: 'non-scaling-stroke',
                            pointerEvents: selectedSectionId === null ? 'none' : 'auto',
                          }}
                          className="cursor-pointer transition-opacity duration-150"
                          onClick={(e) => {
                            e.stopPropagation()
                            // If this seat is in a different section than the one we're zoomed into, zoom to that section first (like top-level).
                            if (shape.id !== selectedSectionId) {
                              handleSectionClickWithZoom(section, area)
                              return
                            }
                            onSeatToggle && onSeatToggle(seat?.seatKey ?? key, seat)
                          }}
                        />
                        {isSelectedSeat ? (
                          <text
                            x={x}
                            y={y + 0.1}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize="1.3"
                            fontWeight="bold"
                            fill="#ffffff"
                            style={{ pointerEvents: 'none', userSelect: 'none' }}
                            >
                             ✓
                            </text>
                          ) : (
                            <text
                              x={x}
                              y={y + 0.1}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fontSize=".55"
                              fontWeight="bold"
                              fill="#ffffff"
                              style={{ pointerEvents: 'none', userSelect: 'none' }}
                              >
                                {seatLabel}
                              </text>
                          )}
                      </React.Fragment>
                    )
                  })}
              </React.Fragment>
            )
          })}
        </svg>
      )}
      </div>
      {onZoomOutToSections && selectedSectionId != null && (
        <ChartZoomOutPill
          onClick={onZoomOutToSections}
          ariaLabel="Back to sections"
        />
      )}
    </div>
  )
});

export default SeatingChart;
